<template>
    <div class="container api-page">
        <div class="row justify-content-center">
            <div class="col-md-8">

                <div class="text-center mb-5 mt-4">
                    <h1 class="display-5 fw-bold">Calendula API</h1>
                    <p class="lead text-muted">The same astronomical engine that powers this website, available as a commercial REST API.</p>
                    <router-link to="/sunrise-sunset" class="btn btn-sm btn-outline-secondary">← Back to Calendula</router-link>
                </div>

                <div class="card border-0 shadow-sm mb-4">
                    <div class="card-body p-4">
                        <h2 class="h5 fw-bold mb-3">What is Calendula API?</h2>
                        <p class="mb-0">
                            Calendula API is a commercial REST API that exposes the full set of astronomical calculations
                            available on <a href="https://calendula.lxcor.com">calendula.lxcor.com</a>.
                            It is designed for developers, researchers, and businesses that need accurate,
                            location-aware ephemeris data integrated directly into their own applications and workflows.
                        </p>
                    </div>
                </div>

                <h2 class="h5 fw-bold mb-3">Available Endpoints</h2>
                <div class="row mb-4">
                    <div class="col-md-6 mb-3" v-for="feature in features" :key="feature.title">
                        <div class="card border-0 h-100" style="background-color: rgba(0,0,0,0.02);">
                            <div class="card-body">
                                <div class="mb-2">
                                    <font-awesome-icon :icon="['fas', feature.icon]" class="text-secondary me-2" />
                                    <strong>{{ feature.title }}</strong>
                                </div>
                                <p class="text-muted mb-0" style="font-size: 14px;">{{ feature.description }}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="card border-0 shadow-sm mb-4">
                    <div class="card-body p-4">
                        <h2 class="h5 fw-bold mb-3">Key Features</h2>
                        <ul class="mb-0" style="line-height: 2;">
                            <li>JSON responses for every endpoint</li>
                            <li>Location-aware — pass latitude, longitude, and timezone</li>
                            <li>Date and date-range queries for all endpoints</li>
                            <li>Global city search and reverse geocoding included</li>
                            <li>Consistent, predictable URL structure</li>
                            <li>API key authentication</li>
                        </ul>
                    </div>
                </div>

                <div class="card border-0 shadow-sm mb-5">
                    <div class="card-body p-4">
                        <h2 class="h5 fw-bold mb-3">Get Access</h2>
                        <p class="text-muted mb-4">
                            Sign up at <a href="https://api.calendula.lxcor.com" target="_blank" rel="noopener">api.calendula.lxcor.com</a> to get started.
                            New accounts receive <strong>1,000 free requests</strong> — no credit card required.
                            When you need more, purchase refillable token packs at any time from your account dashboard.
                        </p>
                        <a href="https://api.calendula.lxcor.com" target="_blank" rel="noopener" class="btn btn-primary me-3">Sign up — 1k free requests</a>
                        <a href="https://api.calendula.lxcor.com" target="_blank" rel="noopener" class="btn btn-outline-secondary">Purchase tokens</a>
                    </div>
                </div>

                <p class="text-center text-muted mb-5" style="font-size: 13px;">
                    <router-link to="/sunrise-sunset">calendula.lxcor.com</router-link>
                </p>

            </div>
        </div>
    </div>
</template>

<script>
const features = [
    {
        icon: 'sun',
        title: 'Sunrise & Sunset',
        description: 'Rise and set times, sun direction, altitude, distance from Earth, and total day length for any date and location.',
    },
    {
        icon: 'moon',
        title: 'Moonrise & Moonset',
        description: 'Rise and set times, moon phase, illumination percentage, direction, altitude, and distance from Earth.',
    },
    {
        icon: 'cloud-sun',
        title: 'Twilight Times',
        description: 'Civil, nautical, and astronomical twilight start and end times — exact dusk and dawn boundaries per day.',
    },
    {
        icon: 'calendar-alt',
        title: 'Moon Phases',
        description: 'New moon, first quarter, full moon, and last quarter dates and times for any month and location.',
    },
    {
        icon: 'leaf',
        title: 'Solstices & Equinoxes',
        description: 'Exact dates and times for the start of each season — spring, summer, autumn, and winter — for any year.',
    },
    {
        icon: 'globe',
        title: 'Planet Positions',
        description: 'Rise, set times, altitude, direction, and distances for all planets visible from a given location and date.',
    },
    {
        icon: 'clock',
        title: 'World Clock',
        description: 'Current local time, UTC offset, and timezone data for cities worldwide.',
    },
    {
        icon: 'map-marker-alt',
        title: 'Geocoding',
        description: 'City search by name and reverse geocoding by coordinates with timezone resolution.',
    },
]

export default {
    name: 'ApiPage',
    data() {
        return { features }
    },
    mounted() {
        document.title = 'Calendula API — Commercial REST API for Astronomical Data'
        const metaDesc = document.querySelector('meta[name="description"]')
        if (metaDesc) metaDesc.setAttribute('content', 'Calendula API provides sunrise/sunset, moon phases, twilight, planet positions, and more as a commercial REST API. The same engine that powers calendula.lxcor.com.')
        const canonical = document.querySelector('link[rel="canonical"]')
        if (canonical) canonical.setAttribute('href', 'https://calendula.lxcor.com/api')
    },
}
</script>

<style scoped>
.api-page {
    text-align: left;
}
</style>
